import { useEffect, useMemo, useState } from "react";
import { getAgeGroups } from "../services/api";

/**
 * The age-group names that should appear in a picker.
 *
 * Source of truth is the platform-wide `age_groups` table, maintained by a
 * platform admin. Sourcing from it means a club never has to configure its own
 * list before it can invite an athlete or price a product — the selector is
 * pre-populated with every group the platform supports.
 *
 * Any names the club (or an existing record) already uses are appended, so
 * custom values that predate the platform list never vanish from the picker
 * and can still be re-selected while editing.
 *
 * Falls back to whatever local names were passed if the request fails, so a
 * network hiccup degrades to the old behaviour instead of an empty dropdown.
 */
export function useAgeGroupOptions(...localNames: (string[] | null | undefined)[]): string[] {
  const [platform, setPlatform] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    getAgeGroups()
      .then((rows) => {
        if (!cancelled) setPlatform(rows.map((r) => r.name));
      })
      .catch(() => {
        /* keep the local names — the picker still works */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // `localNames` is a fresh array each render, so key the memo on its contents.
  const localKey = localNames.map((l) => (l || []).join(" ")).join("");

  return useMemo(() => {
    const out: string[] = [];
    const push = (raw: string | null | undefined) => {
      const name = (raw || "").trim();
      if (name && !out.includes(name)) out.push(name);
    };
    // Platform order first (display_order), then any leftover local values.
    platform.forEach(push);
    localNames.forEach((list) => (list || []).forEach(push));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, localKey]);
}

export default useAgeGroupOptions;
